import { getUserError } from '../../../../components/miscUtils';
import { getCanonicalTypeInfo, getColumnInfo, upsertDerivedColumns } from '../../../../data/analyticsDataSlice';
import { TpDataTable, TpDerivedColumnPatch, isEncoded } from '../../../../data/interfaces';
import {
  filterTypeIds,
  getEnSelectionFilterDef,
  parseEnSelectionFilter,
} from '../../../../data/selection/selectionEnFilters';
import { hydrateFilterStep } from '../../../../data/selection/snapshot/selectionFilterSerialization.deserialize';
import { getDataTable } from '../../charts/chartDataBindings';
import { convertColumnDataListToDisplayString } from '../../helpers/misc';
import { TpDashboardActionContext, TpDbActionStepDefinition as TpDbActionStepTypeDefinition } from '../interfaces';
import { buildDerivedColumnPatch } from '../helpers/derivedColumnPatch';
import { mapSelectionFilterToStorageDTO } from '../../../../data/selection/snapshot/selectionFilterSerialization.serialize';
import { validateLedgerActionAgainstPatches } from '../../../../data/mutation-ledger/helpers';
import { createAddColumnAction, createColumnMetadata } from '../../../../data/mutation-ledger/schemas';
import type { AddColumnTableJoin } from '../../../../data/mutation-ledger/schemas';
import type { TpDataContext } from '../../../../data/interfaces';
import { TpEnSelectionFilterStep } from '../../../../data/selection/interfaces';

export function generateFilterSchema(description: string) {
  return {
    type: 'object',
    description,
    properties: {
      steps: {
        type: 'array',
        items: {
          anyOf: filterTypeIds
            .filter((filterId) => getEnSelectionFilterDef(filterId).getJsonSchema)
            .map((filterId) => getEnSelectionFilterDef(filterId).getJsonSchema!()),
        },
      },
    },
    required: ['steps'],
  };
}

function getJsonSchema(): any {
  return {
    type: 'object',
    title: 'Table Join',
    description: 'Joins columns from a source table to a destination table, matching a common identifier column.',
    properties: {
      type: { type: 'string', const: 'TableJoin' },
      sourceTableId: {
        type: 'string',
        description: 'Identifier of the source table, where the columns are copied from.',
      },
      destTableId: {
        type: 'string',
        description: 'Identifier of the destination table, where the columns are added to.',
      },
      sourceMatchingColId: {
        type: 'string',
        description: 'Identifier of the column in the source table, used for matching.',
      },
      destMatchingColId: {
        type: 'string',
        description: 'Identifier of the column in the destination table, used for matching.',
      },
      aggregationType: {
        type: 'string',
        description: 'Aggregation method (can be: SingleValue).',
      },
      colums: {
        type: 'array',
        description: 'Set of columns to join.',
        items: {
          type: 'object',
          properties: {
            sourceColId: {
              type: 'string',
              description: 'Identifier of the source column.',
            },
            destColId: {
              type: 'string',
              description: 'Identifier of the destination column.',
            },
            destColName: {
              type: 'string',
              description: 'Name of the destination column.',
            },
            destColDescription: {
              type: 'string',
              description: 'Description of the destination column.',
            },
            destGroupId: {
              type: 'string',
              description: 'Identifier of the group for the destination column.',
            },
            destGroupName: {
              type: 'string',
              description: 'Name of the group for the destination column (can be absent if that group already exists).',
            },
            destGroupDescription: {
              type: 'string',
              description:
                'Description of the group for the destination column (can be absent if that group already exists).',
            },
          },
          required: ['sourceColId', 'destColId', 'destColName'],
        },
      },
      sourceFilter: generateFilterSchema(
        'Optional filter definition. The data displayed in this widget will be restricted to this filter.',
      ),
    },
    required: [
      'type',
      'sourceTableId',
      'destTableId',
      'sourceMatchingColId',
      'destMatchingColId',
      'aggregationType',
      'columns',
    ],
  };
}

export function getFilteredRowNrs(ctx: TpDashboardActionContext, dataTable: TpDataTable, filterDef: any): number[] {
  const canonicalTypeInfo = getCanonicalTypeInfo(ctx.dataContext, dataTable.canonicalTypeId);
  const itemList = dataTable.columnData[dataTable.colItemId];

  if (!filterDef) {
    // no filter present => we return all rows
    const allRowNrs = [];
    for (let rowNr = 0; rowNr < itemList.length; rowNr++) allRowNrs.push(rowNr);
    return allRowNrs;
  }

  const filter = parseEnSelectionFilter(ctx.dataContext, dataTable.id, filterDef);

  let filteredMap: { [id: string]: boolean } = {};
  for (const item of itemList) filteredMap[item] = true;
  for (const step of filter.steps) {
    const nextStepFilteredMap: { [id: string]: boolean } = {};
    const stepSelectedItemIds = getEnSelectionFilterDef(step.type).performStep(
      ctx.dataContext,
      canonicalTypeInfo,
      step,
    );
    for (const item of stepSelectedItemIds) if (filteredMap[item]) nextStepFilteredMap[item] = true;
    filteredMap = nextStepFilteredMap;
  }
  const filteredRowNrs = [];
  for (let rowNr = 0; rowNr < itemList.length; rowNr++) if (filteredMap[itemList[rowNr]]) filteredRowNrs.push(rowNr);
  return filteredRowNrs;
}

function aggregate(items: any[], aggregationType: string): any {
  if (aggregationType == 'SingleValue') {
    if (items.length == 0) return NaN;
    if (items.length == 1) return items[0];
    throw getUserError('Aggregation expected a single value');
  }
  throw getUserError(`Invalid aggregation type" ${aggregationType}`);
}

function hydrateFilterStepForDataContext(
  dataContext: TpDataContext,
  dataTable: TpDataTable,
  filterDef: AddColumnTableJoin['sourceFilter'],
): { steps: TpEnSelectionFilterStep[]; warnings: string[] } {
  if (!filterDef) return { steps: [], warnings: [] };

  const canonicalTypeInfo = getCanonicalTypeInfo(dataContext, dataTable.canonicalTypeId);
  const warnings: string[] = [];
  const sanitizedSelectionSteps: TpEnSelectionFilterStep[] = [];
  for (const [index, step] of filterDef.steps.entries()) {
    const { step: sanitizedStep, warnings: stepWarnings } = hydrateFilterStep(step, dataContext, canonicalTypeInfo);
    if (sanitizedStep) {
      sanitizedSelectionSteps.push(sanitizedStep);
    }
    warnings.push(...stepWarnings.map((w) => `step ${index + 1}: ${w}`));
  }
  return { steps: sanitizedSelectionSteps, warnings };
}

function getFilteredRowNrsForDataContext(
  dataContext: TpDataContext,
  dataTable: TpDataTable,
  steps: TpEnSelectionFilterStep[],
): number[] {
  const canonicalTypeInfo = getCanonicalTypeInfo(dataContext, dataTable.canonicalTypeId);
  const itemList = dataTable.columnData[dataTable.colItemId];
  let filteredMap: { [id: string]: boolean } = Object.fromEntries(itemList.map((item: string) => [item, true]));
  for (const step of steps) {
    const nextStepFilteredMap: { [id: string]: boolean } = {};
    const stepSelectedItemIds = getEnSelectionFilterDef(step.type).performStep(dataContext, canonicalTypeInfo, step);
    for (const item of stepSelectedItemIds) if (filteredMap[item]) nextStepFilteredMap[item] = true;
    filteredMap = nextStepFilteredMap;
  }
  return itemList.reduce((rows: number[], item: string, rowNr: number) => {
    if (filteredMap[item]) rows.push(rowNr);
    return rows;
  }, []);
}

export function computeTableJoinPatches(
  dataContext: TpDataContext,
  payload: AddColumnTableJoin,
): { patches: TpDerivedColumnPatch[]; warnings: string[] } {
  const sourceTable = getDataTable(dataContext, payload.sourceTableId);
  const destTable = getDataTable(dataContext, payload.destTableId);
  const sourceMatchingColumnData = convertColumnDataListToDisplayString(
    getColumnInfo(sourceTable, payload.sourceMatchingColId),
    sourceTable.columnData[payload.sourceMatchingColId],
  );
  const destMatchingColumnData = convertColumnDataListToDisplayString(
    getColumnInfo(destTable, payload.destMatchingColId),
    destTable.columnData[payload.destMatchingColId],
  );
  const { steps: sanitizedSelectionSteps, warnings: filterWarnings } = hydrateFilterStepForDataContext(
    dataContext,
    sourceTable,
    payload.sourceFilter,
  );
  const actionWarnings: string[] = [];
  if (filterWarnings.length > 0) {
    const targetColumns = payload.columns.map((col) => col.destColId).join(', ');
    actionWarnings.push(`Could not match the filter for creation of columns [${targetColumns}] perfectly.`);
  }
  const sourceRowNrs = getFilteredRowNrsForDataContext(dataContext, sourceTable, sanitizedSelectionSteps);
  const patches = payload.columns.map((column) => {
    const sourceColInfo = getColumnInfo(sourceTable, column.sourceColId);
    let sourceColumnData = sourceTable.columnData[column.sourceColId];
    if (isEncoded(sourceColInfo.valueType))
      sourceColumnData = convertColumnDataListToDisplayString(sourceColInfo, sourceColumnData);
    const sourceMappedData: { [id: string]: any[] } = {};
    for (const sourceRowNr of sourceRowNrs) {
      const sourceMatchingVal = sourceMatchingColumnData[sourceRowNr];
      if (!sourceMappedData[sourceMatchingVal]) sourceMappedData[sourceMatchingVal] = [];
      sourceMappedData[sourceMatchingVal].push(sourceColumnData[sourceRowNr]);
    }
    const data = destMatchingColumnData.map((id) =>
      sourceMappedData[id] ? aggregate(sourceMappedData[id], payload.aggregationType || 'SingleValue') : NaN,
    );
    const metadata = column.columnMetadata;
    return buildDerivedColumnPatch(
      payload.destTableId,
      column.destColId,
      metadata.name,
      metadata.description,
      sourceColInfo.valueType,
      metadata.groupId,
      metadata.groupName,
      metadata.groupDescription,
      data,
      metadata.decimalDigits,
    );
  });
  return { patches, warnings: filterWarnings };
}

function execute(ctx: TpDashboardActionContext, step: any, onCompleted: () => void) {
  const sourceTableId = step.sourceTableId as string;
  const destTableId = step.destTableId as string;

  const sourceMatchingColId = step.sourceMatchingColId as string;
  const destMatchingColId = step.destMatchingColId as string;
  const aggregationType = step.aggregationType as string;
  const columns = step.columns as {
    sourceColId: string;
    destColId: string;
    destColName: string;
    destColDescription: string | null;
    destGroupId: string | null;
    destGroupName: string | null;
    destGroupDescription: string | null;
  }[];
  const sourceTable = getDataTable(ctx.dataContext, sourceTableId);

  let sourceFilter = null;
  if (step.sourceFilter) sourceFilter = parseEnSelectionFilter(ctx.dataContext, sourceTableId, step.sourceFilter);

  const ledgerAction = createAddColumnAction({
    targetTableId: destTableId,
    targetColumnIds: columns.map((column) => column.destColId),
    payload: {
      type: 'TABLE_JOIN',
      sourceTableId,
      destTableId,
      sourceMatchingColId,
      destMatchingColId,
      aggregationType,
      columns: columns.map((column) => {
        const sourceColInfo = getColumnInfo(sourceTable, column.sourceColId);
        return {
          sourceColId: column.sourceColId,
          destColId: column.destColId,
          columnMetadata: createColumnMetadata({
            name: column.destColName,
            description: column.destColDescription ?? sourceColInfo.description,
            valueType: sourceColInfo.valueType,
            groupId: column.destGroupId,
            groupName: column.destGroupName,
            groupDescription: column.destGroupDescription,
          }),
        };
      }),
      sourceFilter: sourceFilter ? mapSelectionFilterToStorageDTO(ctx.dataContext, sourceFilter) : undefined,
    },
  });
  const { patches, warnings } = computeTableJoinPatches(ctx.dataContext, ledgerAction.payload as AddColumnTableJoin);
  validateLedgerActionAgainstPatches(ledgerAction, patches);
  ctx.dispatch(upsertDerivedColumns({ dataContextId: ctx.dataContext.id, patches, ledgerAction }));
  if (warnings.length > 0) {
    console.warn('Table join warnings:', warnings);
  }
  onCompleted();
}

const dbActionStepTypeTableJoin: TpDbActionStepTypeDefinition = {
  stepType: 'TableJoin',
  execute,
  getJsonSchema,
};

export default dbActionStepTypeTableJoin;
